/**
 * AccountRateCardCard — Account detail ▸ Rate Card (RP-12.4).
 *
 * The firm-wide Rate Card (Settings ▸ Rate Card) prices every Account by default on its
 * default Rate Basis. This card lets Finance record that THIS Account negotiated a
 * different bill rate for a Labor Category — optionally scoped to one Rate Basis, or to
 * every basis when the override carries no Rate Basis (`rateBasisId: null`).
 *
 * Each override is its own row with its own effective dates and reason (RP-12.4) — this
 * is NOT the firm-wide card's whole-card draft/Save model, because there is no cross-row
 * invariant (no 100% mix) to protect here.
 *
 * COST: `avgInternalCost` is ABSENT (not null/blank) from a category without
 * `canViewCost` (non-negotiable 3), so the Cost column is omitted entirely rather than
 * rendered empty. `canManage`/`canViewCost` come straight off the API response — this
 * component never re-derives permission from role.
 *
 * TERMINOLOGY (§3): "Labor Category" never "Role"; "Rate Basis" never "price list" or
 * "rate column"; this is an "Account", never a "Client".
 */

import { Info, Pencil, Plus, X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { CardShell } from "@/components/accounts/AccountProfile";
import { AccountRateOverrideDialog } from "@/components/accounts/AccountRateOverrideDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AccountRateCardView,
  type AccountRateOverrideView,
  useAccountRateCard,
  useDeleteAccountRateOverride,
} from "@/hooks/use-account-rate-overrides";
import type { AccountDefaultRateSource } from "@/hooks/use-accounts";
import { formatRateDelta } from "@/lib/plan-rate-card";
import { cn } from "@/lib/utils";

interface Props {
  accountId: string;
  /**
   * The tier currently SELECTED on the Account page, which may not be the saved one —
   * the segmented control is draft state until Save. Passing it lets this card price
   * itself against the selection straight away, which is the moment you actually want to
   * see what a tier change does to the rates.
   */
  tierId?: string;
  /** Same idea for the Default Rate Card basis picker: an id, "tier", or omitted = saved. */
  basis?: string;
  /** The Default Rate Card currently selected on the Account page (draft-over-saved). */
  defaultRateSource: AccountDefaultRateSource;
}

/** A `numeric(10,2)` money string, or a placeholder dash for null/missing. */
function formatMoney(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : value;
}

/**
 * An effective date is a CALENDAR DATE, so it is formatted in UTC — never local time.
 *
 * The dialog writes these as UTC midnight (`toIsoMidnight`, and it reads them back with a
 * plain `.slice(0, 10)`), so formatting the same instant in local time renders the
 * previous day for every viewer west of UTC: enter "1 Jan 2026", save, and the row reads
 * "Dec 31, 2025". These dates are contractual — the day a negotiated rate starts and stops
 * applying — so they must read identically to what was typed, in every timezone.
 */
function formatDateOnly(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** What the create/edit dialog is open for — null closes it. */
interface DialogState {
  laborCategory: { id: string; name: string };
  override: AccountRateOverrideView | null;
}

export function AccountRateCardCard({ accountId, tierId, basis, defaultRateSource }: Props) {
  const rateCardQuery = useAccountRateCard(accountId, tierId, basis);
  const deleteMutation = useDeleteAccountRateOverride(accountId);

  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [removing, setRemoving] = useState<AccountRateOverrideView | null>(null);

  async function handleRemove() {
    if (!removing) return;
    try {
      await deleteMutation.mutateAsync(removing.id);
      toast.success(`Removed the negotiated rate for ${removing.laborCategoryName}.`);
      setRemoving(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove the negotiated rate.");
    }
  }

  return (
    <CardShell
      title="Rate Card"
      hint={
        rateCardQuery.data
          ? `· new plans default to the ${
              defaultRateSource === "List" ? "Standard" : "Account"
            } Rate Card on ${rateCardQuery.data.defaultRateBasisLabel}`
          : undefined
      }
    >
      {rateCardQuery.isLoading && (
        <div className="space-y-2 py-1">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}

      {rateCardQuery.isError && (
        <ErrorState
          message="Failed to load this Account's Rate Card."
          onRetry={() => rateCardQuery.refetch()}
        />
      )}

      {!rateCardQuery.isLoading && !rateCardQuery.isError && rateCardQuery.data && (
        <RateCardContent
          card={rateCardQuery.data}
          onEdit={(laborCategory, override) => setDialogState({ laborCategory, override })}
          onRemove={setRemoving}
        />
      )}

      {dialogState && (
        <AccountRateOverrideDialog
          open={dialogState !== null}
          onOpenChange={(open) => {
            if (!open) setDialogState(null);
          }}
          accountId={accountId}
          laborCategory={dialogState.laborCategory}
          bases={rateCardQuery.data?.bases ?? []}
          override={dialogState.override}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove negotiated rate?"
        description={
          removing ? `${removing.laborCategoryName} goes back to the list Rate Card rate.` : ""
        }
        confirmLabel="Remove"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleRemove}
      />
    </CardShell>
  );
}

function RateCardContent({
  card,
  onEdit,
  onRemove,
}: {
  card: AccountRateCardView;
  onEdit: (
    laborCategory: { id: string; name: string },
    override: AccountRateOverrideView | null,
  ) => void;
  onRemove: (override: AccountRateOverrideView) => void;
}) {
  const activeCategories = card.categories.filter((c) => c.active);

  if (activeCategories.length === 0) {
    return (
      <EmptyState
        icon={Info}
        title="No Labor Categories on the Rate Card yet"
        description="Add Labor Categories to the firm-wide Rate Card in Settings before negotiating a rate for this Account."
      />
    );
  }

  // Overrides that are NOT the one applied today (a different basis than the default, or
  // no longer in force) are never simply invisible — they surface in a section of their
  // own instead (RP-12.4).
  const appliedIds = new Set(
    activeCategories
      .map((c) => c.appliedOverride?.id)
      .filter((id): id is string => id !== undefined),
  );
  const otherOverrides = card.overrides.filter((o) => !appliedIds.has(o.id));

  return (
    <div className="space-y-4">
      {(card.isTierPreview || card.isBasisPreview) && (
        <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-950">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            Previewing <span className="font-semibold">{card.defaultRateBasisLabel}</span> rates.
            This Account is not saved on that yet —{" "}
            <span className="font-medium">Save Account</span> to apply it. Negotiated rates below
            are this Account's real ones; only the list rates and deltas are being re-priced.
          </span>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Rows left blank price at the list rate on{" "}
        <span className="font-medium text-foreground">{card.defaultRateBasisLabel}</span>. Delta is
        the negotiated rate against that list rate.
      </p>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <Th align="left">Labor Category</Th>
              <Th align="right">List rate</Th>
              <Th align="right">Negotiated rate</Th>
              <Th align="right">Delta</Th>
              {card.canViewCost && <Th align="right">Cost</Th>}
              <Th align="left">Rate Basis</Th>
              <Th align="left">Effective</Th>
              <Th align="left">Source</Th>
              {card.canManage && <th className="px-3 py-2" aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {activeCategories.map((category) => (
              <CategoryRow
                key={category.laborCategoryId}
                category={category}
                canManage={card.canManage}
                canViewCost={card.canViewCost}
                onEdit={onEdit}
                onRemove={onRemove}
              />
            ))}
          </tbody>
        </table>
      </div>

      {otherOverrides.length > 0 && <OtherOverridesSection overrides={otherOverrides} />}
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align: "left" | "right" }) {
  return (
    <th
      className={cn(
        "px-3 py-2 text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

function CategoryRow({
  category,
  canManage,
  canViewCost,
  onEdit,
  onRemove,
}: {
  category: AccountRateCardView["categories"][number];
  canManage: boolean;
  canViewCost: boolean;
  onEdit: (
    laborCategory: { id: string; name: string },
    override: AccountRateOverrideView | null,
  ) => void;
  onRemove: (override: AccountRateOverrideView) => void;
}) {
  const override = category.appliedOverride;
  const laborCategory = { id: category.laborCategoryId, name: category.laborCategoryName };
  const delta = formatRateDelta(category.cardRate, override?.billRate ?? null);

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/20">
      <td className="px-3 py-2 font-medium">{category.laborCategoryName}</td>
      <td
        className={cn(
          "px-3 py-2 text-right tabular-nums",
          override && "text-muted-foreground line-through",
        )}
      >
        {formatMoney(category.cardRate)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {override ? (
          <span className="font-semibold text-primary" title={`Reason: ${override.reason}`}>
            {formatMoney(override.billRate)}
          </span>
        ) : (
          <span className="text-muted-foreground">At list</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {delta === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span
            className={cn(
              "font-medium",
              delta.direction === "discount" && "text-warning-700",
              delta.direction === "premium" && "text-success",
              delta.direction === "level" && "text-muted-foreground",
            )}
          >
            {delta.text}
          </span>
        )}
      </td>
      {canViewCost && (
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
          {category.avgInternalCost !== undefined ? formatMoney(category.avgInternalCost) : "—"}
        </td>
      )}
      <td className="px-3 py-2 text-muted-foreground">
        {override ? (override.rateBasisCode ?? "Every basis") : "—"}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {override
          ? `${formatDateOnly(override.effectiveStart)} – ${
              override.effectiveEnd ? formatDateOnly(override.effectiveEnd) : "open"
            }`
          : "—"}
      </td>
      {/* The contract or order form the rate came from — what Finance checks it against
          (RP-12.6). The reason moved to the negotiated rate's tooltip to make room. */}
      <td
        className="max-w-[220px] truncate px-3 py-2 text-muted-foreground"
        title={override?.sourceDocumentReference ?? undefined}
      >
        {override?.sourceDocumentReference ?? "—"}
      </td>
      {canManage && (
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label={`${override ? "Edit" : "Add"} negotiated rate for ${category.laborCategoryName}`}
              onClick={() => onEdit(laborCategory, override)}
            >
              {override ? <Pencil className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
            {override && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                aria-label={`Remove negotiated rate for ${category.laborCategoryName}`}
                onClick={() => onRemove(override)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

/** Non-default-basis and expired overrides — never invisible, just out of the main table. */
function OtherOverridesSection({ overrides }: { overrides: AccountRateOverrideView[] }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <p className="mb-2 text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Other bases and past rates
      </p>
      <ul className="space-y-1.5">
        {overrides.map((o) => (
          <li key={o.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate">
              <span className="font-medium">{o.laborCategoryName}</span>{" "}
              <span className="text-muted-foreground">
                · {o.rateBasisName ?? "Every basis"} · {formatMoney(o.billRate)} ·{" "}
                {formatDateOnly(o.effectiveStart)}
                {o.effectiveEnd ? ` – ${formatDateOnly(o.effectiveEnd)}` : ""}
              </span>
            </span>
            <Badge variant={o.inForce ? "secondary" : "outline"} className="shrink-0 text-xs">
              {o.inForce ? "Active" : "Expired"}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
